<template>
    <v-container fluid>
        <v-row>
            <v-col cols="12">
                <v-card :loading="loading" class="pa-4">
                    <v-card-title>
                        {{ $t('administration.konfiguration.startupmessage.title') }}
                    </v-card-title>

                    <v-card-text>
                        <v-alert type="info" variant="tonal" class="mb-4">
                            {{ $t('administration.konfiguration.startupmessage.information') }}
                        </v-alert>
                    </v-card-text>

                    <v-card-text>
                        <v-form ref="form">
                            <v-row>
                                <v-col cols="12">
                                    <v-textarea
                                        v-model="startupMessage.messageDe"
                                        :label="$t('administration.konfiguration.startupmessage.messageDE')"
                                        variant="outlined"
                                        rows="5"
                                        hide-details="auto"
                                        class="mb-4"
                                        maxlength="1000"
                                    ></v-textarea>
                                </v-col>
                                <v-col cols="12">
                                    <v-textarea
                                        v-model="startupMessage.messageFr"
                                        :label="$t('administration.konfiguration.startupmessage.messageFR')"
                                        variant="outlined"
                                        rows="5"
                                        hide-details="auto"
                                        class="mb-4"
                                        maxlength="1000"
                                    ></v-textarea>
                                </v-col>
                                <v-col cols="12" sm="6" md="4">
                                    <v-text-field
                                        v-model="validUntilFormatted"
                                        :label="$t('administration.konfiguration.startupmessage.validUntil')"
                                        type="datetime-local"
                                    />
                                </v-col>
                            </v-row>
                        </v-form>
                    </v-card-text>
                    <v-card-actions class="px-4 pb-4">
                        <v-spacer></v-spacer>
                        <v-btn color="secondary" variant="flat" :disabled="loading" class="mr-2" @click="preview">
                            {{ $t('administration.konfiguration.startupmessage.preview') }}
                        </v-btn>
                        <v-btn color="primary" variant="flat" :loading="loading" :disabled="loading" @click="save">
                            {{ $t('administration.konfiguration.startupmessage.save') }}
                        </v-btn>
                    </v-card-actions>
                </v-card>
            </v-col>
        </v-row>
    </v-container>
</template>

<script lang="ts">
import { Component, Vue, Watch } from 'vue-facing-decorator';
import { namespace } from 'vuex-facing-decorator';
import { StartupMessage, startupMessageService } from '@/services/startupMessageService';

import { NAME as NAME_SECURITY } from '@/store/modules/security';
import { NAME as NAME_HEADERNOTIFICATION } from '@/store/modules/headerNotification';
import { NAME as NAME_GENERAL } from '@/store/modules/general';

const securityModul = namespace(NAME_SECURITY);
const headerModul = namespace(NAME_HEADERNOTIFICATION);
const generalModul = namespace(NAME_GENERAL);

import { SUBTITLE, FOOTERTITLE } from '@/store/modules/general/mutations';
import { HASACCESS } from '@/store/modules/security/getters';
import { SETACCESSDENIED, STARTUP_MESSAGE, CHANGEMODALSTARTUPMESSAGEVISIBLE } from '@/store/modules/security/mutations';
import { NEW_SUCESS, NEW_ERRORMESSAGES } from '@/store/modules/headerNotification/actions';

import NowhowDatepicker from '@/components/NowhowDatepicker.vue';

@Component({
    components: {
        NowhowDatepicker
    }
})
export default class Konfiguration extends Vue {
    @generalModul.Mutation(SUBTITLE) setSubTitle!: Function;
    @generalModul.Mutation(FOOTERTITLE) setFooterTitle!: Function;

    @securityModul.Getter(HASACCESS) hasAccess!: Function;
    @securityModul.Mutation(SETACCESSDENIED) setAccessDenied!: Function;
    @securityModul.Mutation(STARTUP_MESSAGE) setStartupMessage!: Function;
    @securityModul.Mutation(CHANGEMODALSTARTUPMESSAGEVISIBLE) setStartupMessageVisible!: Function;

    @headerModul.Action(NEW_SUCESS) successMessage!: Function;
    @headerModul.Action(NEW_ERRORMESSAGES) errorMessage!: Function;

    loading = false;
    startupMessage: StartupMessage = {
        messageDe: '',
        messageFr: '',
        validUntil: null
    };

    validUntilFormatted = '';

    @Watch('validUntilFormatted')
    onValidUntilFormattedChanged(val: string) {
        if (!val) {
            this.startupMessage.validUntil = null;
            return;
        }

        // HTML5 datetime-local input format: YYYY-MM-DDTHH:mm
        try {
            const date = new Date(val);
            if (!isNaN(date.getTime())) {
                this.startupMessage.validUntil = val;
            }
        } catch (e) {
            this.startupMessage.validUntil = null;
        }
    }

    created() {
        this.setSubTitle(this.$t('administration.konfiguration.subtitle') || 'Konfiguration');
        this.setFooterTitle('S960');

        if (!this.hasAccess('Navigation.Konfiguration')) {
            this.setAccessDenied(true);
            this.errorMessage({ errorMessages: this.$t('errormessage.noaccesspage') });
        } else {
            this.setAccessDenied(false);
            this.load();
        }
    }

    async load() {
        this.loading = true;
        try {
            const data = await startupMessageService.getStartupMessage();
            if (data) {
                this.startupMessage = data;
                if (data.validUntil) {
                    const date = new Date(data.validUntil);
                    // Format for datetime-local: YYYY-MM-DDTHH:mm
                    const year = date.getFullYear();
                    const month = String(date.getMonth() + 1).padStart(2, '0');
                    const day = String(date.getDate()).padStart(2, '0');
                    const hours = String(date.getHours()).padStart(2, '0');
                    const minutes = String(date.getMinutes()).padStart(2, '0');
                    this.validUntilFormatted = `${year}-${month}-${day}T${hours}:${minutes}`;
                }
            }
        } catch (e) {
            console.error(e);
            this.errorMessage({ errorMessages: this.$t('errormessage.load.failed') || 'Fehler beim Laden der Daten' });
        } finally {
            this.loading = false;
        }
    }

    async save() {
        this.loading = true;
        try {
            await startupMessageService.updateStartupMessage(this.startupMessage);
            this.successMessage({ text: this.$t('administration.konfiguration.startupmessage.save.success') || 'Konfiguration erfolgreich gespeichert' });
        } catch (e) {
            console.error(e);
            this.errorMessage({ errorMessages: this.$t('errormessage.save.failed') || 'Fehler beim Speichern der Konfiguration' });
        } finally {
            this.loading = false;
        }
    }

    preview() {
        // Commit current form data to store as temporary startup message
        this.setStartupMessage({ ...this.startupMessage });
        // Show the dialog
        this.setStartupMessageVisible(true);
    }
}
</script>
